import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import React, { useMemo } from 'react';
import { Cell, Label, Pie, PieChart, ResponsiveContainer } from 'recharts';

interface Props {
  title: string;
  percentage: number;
  color: 'green' | 'orange' | 'red' | 'blue';
  height?: string;
  width?: string;
  innerRadius?: number;
  outerRadius?: number;
  isRatio?: boolean;
  consequent?: number;
}

const testData = [{ name: 'Group A', value: 100 }];

export default function SimpleGaugeGraph({
  title,
  percentage,
  color: colorProp,
  height = '200px',
  width = '200px',
  innerRadius = 40,
  outerRadius = 60,
  isRatio,
  consequent = 0,
}: Props) {
  const t = useTheme();

  const color = useMemo(() => {
    switch (colorProp) {
      case 'green':
        return t.palette.success.main;
      case 'orange':
        return t.palette.warning.main;
      case 'red':
        return t.palette.error.main;
      default:
        return t.palette.info.main;
    }
  }, [
    colorProp,
    t.palette.error.main,
    t.palette.info.main,
    t.palette.success.main,
    t.palette.warning.main,
  ]);

  const actualAngle = useMemo(() => {
    if (isRatio) {
      return percentage * 360;
    } else {
      return (percentage * 360) / 100;
    }
  }, [isRatio, percentage]);

  return (
    <Paper sx={{ p: '10px', width: width, height: height }}>
      <Box display="flex" mb={1}>
        <Box flexGrow={1}>
          <Typography component="div" variant="body2" textAlign="center" fontWeight="bold">
            {title}
          </Typography>
        </Box>
      </Box>
      <ResponsiveContainer height={150} width={150} style={{ margin: 'auto' }}>
        <PieChart height={150} width={150}>
          <Pie
            data={testData}
            dataKey="value"
            startAngle={90}
            endAngle={-270}
            innerRadius={innerRadius}
            outerRadius={outerRadius}
          >
            <Cell fill={t.palette.text.disabled} />
          </Pie>
          <Pie
            data={testData}
            dataKey="value"
            startAngle={90}
            endAngle={90 - actualAngle}
            innerRadius={innerRadius}
            outerRadius={outerRadius}
          >
            <Label
              fontSize={20}
              value={
                isRatio
                  ? percentage === 0
                    ? `0 : ${consequent}`
                    : `${percentage} : ${consequent}`
                  : `${percentage}%`
              }
              position="center"
              fill={t.palette.text.primary}
            />

            <Cell fill={color} />
          </Pie>
        </PieChart>
      </ResponsiveContainer>
    </Paper>
  );
}
